import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatMoney } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Receipt as ReceiptIcon, ArrowRight, ArrowLeft } from "lucide-react";

export default function Facturacion() {
  const [orders, setOrders] = useState([]);
  const navigate = useNavigate();
  const [lastPedidoId, setLastPedidoId] = useState(null);

  useEffect(() => {
    try {
      const pid = sessionStorage.getItem("last_pedido_id");
      if (pid) setLastPedidoId(pid);
    } catch { /* ignore */ }
  }, []);

  const load = async () => {
    // Contabilidad R31b: módulo filtra por fecha > fecha/hora exacta último cierre.
    let after = null;
    try {
      const { data: counts } = await api.get("/stats/pedidos-corte");
      after = counts?.ultimo_cierre_at || null;
    } catch { /* ignore */ }
    const { data } = await api.get("/orders");
    const filtered = after ? data.filter((o) => (o.fecha || "") > after) : data;
    setOrders(filtered);
  };
  useEffect(() => { load(); }, []);

  // Order buckets: only orders that have some facturación activity
  const withFacturas = orders.filter((o) => (o.facturas || []).length > 0);
  const pendientes = withFacturas.filter((o) => Number(o.saldo_pendiente ?? 0) > 0.005);
  const pagados = withFacturas.filter((o) => Number(o.saldo_pendiente ?? 0) <= 0.005);
  const today = new Date().toISOString().slice(0, 10);
  const hoy = withFacturas.filter((o) => (o.facturas || []).some((f) => (f.fecha_pago || "").slice(0, 10) === today));

  const totalPend = pendientes.reduce((s, o) => s + Number(o.saldo_pendiente ?? 0), 0);
  const totalPag = pagados.reduce((s, o) => s + Number(o.monto_facturado_acum ?? o.total ?? 0), 0);
  const totalHoy = hoy.reduce((s, o) => s + (o.facturas || []).filter((f) => (f.fecha_pago || "").slice(0, 10) === today).reduce((ss, f) => ss + Number(f.monto || 0), 0), 0);

  return (
    <div className="space-y-6" data-testid="facturacion-page">
      <header>
        <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500 font-semibold">Cobranza</div>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <h1 className="font-manrope font-black tracking-tight text-4xl mt-1 flex items-center gap-3">
            <ReceiptIcon className="w-8 h-8 text-[#10B981]" /> Facturación
          </h1>
          {lastPedidoId && (
            <button
              type="button"
              onClick={() => {
                try { sessionStorage.removeItem("last_pedido_id"); } catch { /* ignore */ }
                navigate(`/pedidos/${lastPedidoId}`);
              }}
              className="inline-flex items-center gap-2 rounded-md bg-[#002FA7] hover:bg-[#00227A] text-white text-sm px-4 py-2 mt-1"
              data-testid="btn-volver-pedido"
            >
              <ArrowLeft className="w-4 h-4" /> Volver al pedido
            </button>
          )}
        </div>
        <p className="text-zinc-500 mt-2 text-sm">Pagos parciales y facturas por pedido.</p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-5 border-zinc-200 rounded-md">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Cobrado hoy</div>
          <div className="mt-1 font-manrope font-black text-3xl text-indigo-700" data-testid="total-hoy">{formatMoney(totalHoy)}</div>
          <div className="text-xs text-zinc-500 mt-1">{hoy.length} pedido{hoy.length === 1 ? "" : "s"} con pagos hoy</div>
        </Card>
        <Card className="p-5 border-zinc-200 rounded-md">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Saldo pendiente total</div>
          <div className="mt-1 font-manrope font-black text-3xl text-orange-700">{formatMoney(totalPend)}</div>
          <div className="text-xs text-zinc-500 mt-1">{pendientes.length} pedido{pendientes.length === 1 ? "" : "s"} con saldo</div>
        </Card>
        <Card className="p-5 border-zinc-200 rounded-md">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Cobrado</div>
          <div className="mt-1 font-manrope font-black text-3xl text-emerald-700">{formatMoney(totalPag)}</div>
          <div className="text-xs text-zinc-500 mt-1">{pagados.length} pedido{pagados.length === 1 ? "" : "s"} pagado{pagados.length === 1 ? "" : "s"}</div>
        </Card>
      </div>

      <Section title="Facturación de hoy" data={hoy} showFactura extraTotals={{
        label: `Total facturado hoy: ${formatMoney(totalHoy)}`,
        bancos: (() => {
          const map = {};
          hoy.forEach((o) => (o.facturas || []).forEach((f) => {
            if ((f.fecha_pago || "").slice(0, 10) !== today) return;
            const k = (f.banco || "").trim() || (f.metodo_pago || "").trim() || "—";
            map[k] = (map[k] || 0) + Number(f.monto || 0);
          }));
          return Object.entries(map).map(([nombre, total]) => ({ nombre, total })).sort((a, b) => b.total - a.total);
        })(),
      }} />
      <Section title="Pendientes / Parciales" data={pendientes} showSaldo showFactura />
      <Section title="Pagados" data={pagados} showFactura />
    </div>
  );
}

function Section({ title, data, showFactura = false, showSaldo = false, extraTotals = null }) {
  const cols = 6 + (showFactura ? 1 : 0) + (showSaldo ? 1 : 0);
  return (
    <Card className="border-zinc-200 rounded-md overflow-hidden">
      <div className="p-5 border-b border-zinc-200 flex flex-col md:flex-row md:items-end md:justify-between gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Sección</div>
          <div className="text-xl font-semibold mt-0.5">{title}</div>
        </div>
        {extraTotals && (
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="rounded-md bg-indigo-50 border border-indigo-200 text-indigo-900 px-3 py-1.5 font-semibold" data-testid="section-total">
              {extraTotals.label}
            </span>
            {(extraTotals.bancos || []).map((b) => (
              <span key={b.nombre} className="rounded-md bg-emerald-50 border border-emerald-200 text-emerald-900 px-2 py-1" data-testid={`banco-tot-${b.nombre}`}>
                <strong>{b.nombre}</strong>: {formatMoney(b.total)}
              </span>
            ))}
          </div>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-zinc-600">
            <tr className="text-left">
              <th className="px-5 py-3 text-xs uppercase tracking-widest font-semibold">Pedido</th>
              <th className="px-5 py-3 text-xs uppercase tracking-widest font-semibold">Facturas</th>
              {showFactura && <th className="px-5 py-3 text-xs uppercase tracking-widest font-semibold">N° Factura(s)</th>}
              <th className="px-5 py-3 text-xs uppercase tracking-widest font-semibold">Cliente</th>
              <th className="px-5 py-3 text-xs uppercase tracking-widest font-semibold text-right">Total</th>
              <th className="px-5 py-3 text-xs uppercase tracking-widest font-semibold text-right">Facturado</th>
              {showSaldo && <th className="px-5 py-3 text-xs uppercase tracking-widest font-semibold text-right">Saldo</th>}
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {data.length === 0 && (
              <tr><td colSpan={cols} className="text-center text-zinc-400 py-8">Sin registros.</td></tr>
            )}
            {data.map((o) => {
              const facts = o.facturas || [];
              const acum = Number(o.monto_facturado_acum || 0);
              const saldo = Number(o.saldo_pendiente ?? Math.max(0, (o.total || 0) - acum));
              return (
                <tr key={o.id} className="hover:bg-zinc-50 align-top">
                  <td className="px-5 py-3 font-mono text-xs">{o.numero}</td>
                  <td className="px-5 py-3 text-xs text-zinc-600">{facts.length}</td>
                  {showFactura && (
                    <td className="px-5 py-3 font-mono text-xs font-semibold text-indigo-800" data-testid={`fact-numero-${o.numero}`}>
                      {facts.length > 0
                        ? facts.map((f) => f.numero_factura).join(", ")
                        : (o.factura_data?.numero_factura || <span className="text-zinc-400">—</span>)}
                    </td>
                  )}
                  <td className="px-5 py-3">{o.cliente_nombre}</td>
                  <td className="px-5 py-3 text-right tabular-nums">{formatMoney(o.total)}</td>
                  <td className="px-5 py-3 text-right tabular-nums text-emerald-700 font-semibold">{formatMoney(acum)}</td>
                  {showSaldo && (
                    <td className="px-5 py-3 text-right tabular-nums font-semibold text-orange-700">{formatMoney(saldo)}</td>
                  )}
                  <td className="px-5 py-3 text-right">
                    <Link to={`/pedidos/${o.id}#crear-factura`} className="text-[#002FA7] text-xs font-semibold uppercase tracking-widest inline-flex items-center gap-1">
                      {saldo > 0 ? "Facturar" : "Ver"} <ArrowRight className="w-3 h-3" />
                    </Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
